import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'

import styles from './styles.module.scss'

import { isApiError } from '@/api'
import {
  AUDIT_LOG_MAX_WINDOW_DAYS,
  createAuditLogsPortalLink,
  getWorkosAuditEvents,
  WORKOS_AUDIT_EVENTS_PAGE_SIZES,
  WorkosAuditEventsPageSize,
} from '@/api/auditLogs'
import { AuditAction, DomainVerificationStatus, WorkosAuditLogEntry } from '@/api/generated/api'
import Button from '@/components/Button'
import { useToast } from '@/components/Toast/ToastContext'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAnalytics } from '@/hooks/useAnalytics'
import DateRangePicker from '@/pages/app/AuditLogs/DateRangePicker'
import { PRESET_DAYS, presetRange } from '@/pages/app/AuditLogs/presets'
import EventTypeFilter, {
  ACTION_LABELS,
  ALL_EVENT_TYPES,
} from '@/pages/app/AuditLogs/WorkosAuditEventsSection/EventTypeFilter'
import { domainVerificationsQuery } from '@/pages/app/Settings/components/DomainVerification/queries'
import { downloadBlob, downloadCSV } from '@/utils/csv'
import { EventName } from '@/utils/eventDefinitions'
import { seatTypeTitle } from '@/utils/seatType'

const DEFAULT_WINDOW_DAYS = 30
const DEFAULT_PAGE_SIZE = 20
// The manual Refresh button collapses to page 1 before refetching (see handleRefresh below) —
// auto-refresh reuses that exact behavior on a timer rather than react-query's own
// refetchInterval, which would refetch every currently-loaded page on each tick.
const AUTO_REFRESH_INTERVAL_MS = 30_000
// Cursor-pagination safety cap, mirroring the real export's MAX_EXPORT_PAGES — a runaway
// range can't loop forever; the user is told to narrow the window for the rest.
const MAX_EXPORT_PAGES = 100
// Export always requests the largest page size regardless of the on-screen picker — it's a
// throughput knob for the loop below, not something the user's viewer-page-size choice should
// silently affect.
const EXPORT_PAGE_SIZE = 100

export { ACTION_LABELS, ALL_EVENT_TYPES }

// `row.action` is a raw wire string, not narrowed to `AuditAction` — an unmapped/legacy action
// falls back to itself rather than failing the exhaustive `ACTION_LABELS` lookup above. Row
// sentence reads "<actor> | <rowLabel> | <target>", e.g. "Admin Wispr | removed member | Margaret
// Hamilton" — verb-first phrasing, distinct from ACTION_LABELS' standalone `filterLabel`.
const actionLabel = (action: string): string => ACTION_LABELS[action as AuditAction]?.rowLabel ?? action

// Settings-change actions carry their detail in `raw_data.metadata` (setting_key/previous_value/
// new_value/source — see aria-web's ORGANIZATION_SETTING_CHANGE_METADATA_SCHEMA); member.role_changed
// carries old_role/new_role instead (+ team_id/team_name when scoped to one team, see
// MEMBER_ROLE_CHANGE_METADATA_SCHEMA), and member.seat_changed carries old_seat/new_seat (empty old_seat
// = no seat before) — every other action has no per-row detail today, so this reads back `null` for them.
const getSettingChangeDetail = (row: WorkosAuditLogEntry): string | null => {
  const data = row.raw_data as { metadata?: unknown } | null
  const metadata = data?.metadata as Record<string, unknown> | undefined
  if (!metadata) return null
  if (typeof metadata.setting_key === 'string') {
    return `${metadata.setting_key}: ${metadata.previous_value ?? '—'} → ${metadata.new_value ?? '—'}`
  }
  if (typeof metadata.new_seat === 'string') {
    const seatLabel = (seat: unknown) => (typeof seat === 'string' && seat ? (seatTypeTitle(null, seat) ?? seat) : null)
    return `${seatLabel(metadata.old_seat) ?? 'No seat'} → ${seatLabel(metadata.new_seat) ?? '—'}`
  }
  if (typeof metadata.new_role === 'string') {
    const roleChange = `${metadata.old_role || '—'} → ${metadata.new_role}`
    return typeof metadata.team_name === 'string' && metadata.team_name
      ? `${roleChange} (in team ${metadata.team_name})`
      : roleChange
  }
  return null
}

type ExportFormat = 'csv' | 'json'

const CSV_COLUMNS = ['Timestamp', 'Actor', 'Action', 'Target', 'Details', 'Event ID']

// CSV is the curated, human-facing column set; JSON keeps the full WorkosAuditLogEntry
// (incl. the raw WorkOS payload) for anyone who needs it.
const toCsvRows = (rows: WorkosAuditLogEntry[]): string[][] => [
  CSV_COLUMNS,
  ...rows.map((r) => [
    r.timestamp,
    r.actor_name ?? r.actor_id ?? '',
    actionLabel(r.action),
    r.target_name ?? r.target_id ?? '',
    getSettingChangeDetail(r) ?? '',
    r.event_id,
  ]),
]

const toIso = (date: Date) => format(date, 'yyyy-MM-dd')

async function fetchAllWorkosAuditEventRows(
  start: string,
  end: string,
  eventTypes: AuditAction[]
): Promise<{ rows: WorkosAuditLogEntry[]; capped: boolean; truncated: boolean }> {
  // De-duped by event_id for the same reason the on-screen table is: consecutive pages can
  // share exactly one boundary row (the date-range narrowing this pagination uses is inclusive
  // at the exact instant).
  const seen = new Set<string>()
  const collected: WorkosAuditLogEntry[] = []
  let before: string | undefined
  let capped = false
  let truncated = false

  for (let page = 0; page < MAX_EXPORT_PAGES; page++) {
    const { data } = await getWorkosAuditEvents({ start, end, eventTypes, before, limit: EXPORT_PAGE_SIZE })
    for (const row of data.rows) {
      if (seen.has(row.event_id)) continue
      seen.add(row.event_id)
      collected.push(row)
    }
    truncated = truncated || !!data.truncated
    if (!data.before) break
    before = data.before
    if (page === MAX_EXPORT_PAGES - 1) capped = true
  }

  return { rows: collected, capped, truncated }
}

const NotOptedInMessage = ({
  domains,
  domainsLoaded,
}: {
  domains: DomainVerificationStatus[] | undefined
  domainsLoaded: boolean
}) => {
  // `domains` is also `undefined` while the domain-status query is still loading or has failed —
  // only a genuinely successful, empty response means "no domains on file".
  if (domainsLoaded && domains && domains.length === 0) {
    return (
      <>
        Your organization is not linked to any domains. Contact{' '}
        <a href="mailto:enterprise@wispr.ai">enterprise@wispr.ai</a> to add one.
      </>
    )
  }
  if (domainsLoaded && domains && !domains.some((d) => d.verified)) {
    return (
      <>
        Audit events start automatically once a domain is verified. Verify a domain under Settings →{' '}
        <Link to="/app/settings/organization">Organization</Link>.
      </>
    )
  }
  return (
    <>
      Audit events are being set up for your organization. Contact{' '}
      <a href="mailto:enterprise@wispr.ai">enterprise@wispr.ai</a> if this persists.
    </>
  )
}

// Reads live from WorkOS — this is the Events tab's read path (the local derived-read table has
// no UI consumer left; see aria-web's services/audit_log/workos_audit_events.py).
const WorkosAuditEventsSection = () => {
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const [today, setToday] = useState(() => new Date())
  const [startDate, setStartDate] = useState(() => toIso(subDays(today, DEFAULT_WINDOW_DAYS)))
  const [endDate, setEndDate] = useState(() => toIso(today))
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<AuditAction>>(new Set(ALL_EVENT_TYPES))
  const [pageSize, setPageSize] = useState<WorkosAuditEventsPageSize>(DEFAULT_PAGE_SIZE)
  const noEventTypesSelected = selectedEventTypes.size === 0

  const validationError = useMemo(() => {
    if (!startDate || !endDate) return 'Select both a start and end date.'
    if (startDate > endDate) return 'Start date must be on or before the end date.'
    if (differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) >= AUDIT_LOG_MAX_WINDOW_DAYS) {
      return `Date range must be shorter than ${AUDIT_LOG_MAX_WINDOW_DAYS} days.`
    }
    return null
  }, [startDate, endDate])

  const eventTypesKey = Array.from(selectedEventTypes).sort().join(',')
  const queryKey = ['audit-logs', 'workos', startDate, endDate, eventTypesKey, pageSize]
  const queryClient = useQueryClient()

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
      (
        await getWorkosAuditEvents({
          start: startDate,
          end: endDate,
          eventTypes: Array.from(selectedEventTypes),
          before: pageParam,
          limit: pageSize,
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.before || undefined,
    enabled: !validationError && !noEventTypesSelected,
    // Keeps the previous key's `data` around while a new one (date range/event types/page size
    // change) fetches, so `isLoading` (isPending && isFetching) only means "the very first fetch
    // this component has ever made" — without this, isPending resets to true for every new
    // queryKey with no cache, and the filter bar would flash away on every filter change too.
    placeholderData: keepPreviousData,
  })

  // De-duped defensively by event_id: consecutive pages can share exactly one boundary row
  // (the date-range narrowing this pagination uses is confirmed inclusive at the exact instant
  // — see the backend module's docstring) — this is what stops it from rendering twice.
  const rows = useMemo(() => {
    const seen = new Set<string>()
    const deduped: WorkosAuditLogEntry[] = []
    for (const row of query.data?.pages.flatMap((page) => page.rows) ?? []) {
      if (seen.has(row.event_id)) continue
      seen.add(row.event_id)
      deduped.push(row)
    }
    return deduped
  }, [query.data])

  // `today` (and the default "Last N days" window derived from it) is only computed once, on
  // mount -- neither Refresh nor the auto-refresh timer re-derive it on their own, so a tab left
  // open silently stops showing new events once real time crosses the frozen `endDate`. If the
  // current range still matches one of the quick presets (the user hasn't picked a custom range),
  // slide it forward to end "now" before refetching; a custom range is left untouched. Returns
  // whether the window moved -- the queryKey changing already triggers a fresh page-1 fetch, so
  // callers should skip their own refetch in that case to avoid racing two fetches.
  const advanceTodayIfOnDefaultWindow = (): boolean => {
    const now = new Date()
    const activePresetDays = PRESET_DAYS.find((d) => {
      const range = presetRange(d, today)
      return startDate === range.start && endDate === range.end
    })
    setToday(now)
    if (activePresetDays === undefined) return false
    const nextRange = presetRange(activePresetDays, now)
    if (nextRange.start === startDate && nextRange.end === endDate) return false
    setStartDate(nextRange.start)
    setEndDate(nextRange.end)
    return true
  }

  // Refresh means "start over," not "replay every loaded page's stored boundary" — the latter
  // can go stale if a new event landed near an already-fetched page boundary between loads.
  // Collapse back to page 1 before refetching so there's no stale page-2+ boundary left to
  // replay against a first page whose contents may have shifted.
  const handleRefresh = async () => {
    if (noEventTypesSelected) return
    if (advanceTodayIfOnDefaultWindow()) return
    queryClient.setQueryData(queryKey, (data: typeof query.data) =>
      data ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data
    )
    await query.refetch()
  }

  // Auto-refresh: unlike the manual Refresh button, this does NOT collapse to page 1 — losing
  // pages you'd already loaded on every tick was worse than the request cost of refetching all
  // of them (react-query's own default useInfiniteQuery.refetch() behavior: it replays every
  // currently-loaded page, re-deriving each page's cursor from the freshly-fetched previous one).
  // Reads fetch status fresh from the query client at tick time (not a closed-over
  // `query.isFetching`) so it can't skip a tick based on a stale snapshot from whenever this
  // effect last ran.
  useEffect(() => {
    const interval = setInterval(() => {
      // A background tab still ticks; replaying every loaded page against a third-party API for
      // nobody to see is pure cost. Resumes on its own next tick once the tab is visible again.
      if (document.visibilityState !== 'visible') return
      if (validationError || noEventTypesSelected) return
      if (queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching') return
      if (advanceTodayIfOnDefaultWindow()) return
      void query.refetch()
    }, AUTO_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, eventTypesKey, pageSize])

  const exportMutation = useMutation({
    mutationFn: async (exportFormat: ExportFormat) => {
      const result = await fetchAllWorkosAuditEventRows(startDate, endDate, Array.from(selectedEventTypes))
      return { ...result, format: exportFormat }
    },
    onSuccess: ({ rows, capped, truncated, format }) => {
      const filename = `audit-events-${startDate}_to_${endDate}.${format}`
      if (format === 'csv') {
        downloadCSV(toCsvRows(rows), filename)
      } else {
        downloadBlob(new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' }), filename)
      }
      // Fired after the export succeeds, not before — a failed export shouldn't count.
      trackEvent?.(EventName.AuditLogsExported, {
        row_count: rows.length,
        event_type_count: selectedEventTypes.size,
        capped: capped || truncated,
        format,
      })
      if (capped) {
        updateToast(
          `Exported the first ${rows.length.toLocaleString()} events. Narrow the date range to export the rest.`,
          'success',
          7000
        )
      } else if (truncated) {
        updateToast(
          `Exported ${rows.length.toLocaleString()} events. Some older events in this range share an exact timestamp and couldn't be reached — narrow the date range to see them.`,
          'success',
          8000
        )
      } else if (rows.length === 0) {
        updateToast('No audit events found in this range. Downloaded an empty file.', 'success', 5000)
      } else {
        updateToast(`Exported ${rows.length.toLocaleString()} events.`, 'success', 4000)
      }
    },
    onError: (error: unknown) => {
      const message = isApiError(error) ? error.message : 'Failed to export audit events. Please try again.'
      updateToast(message, 'error', 6000)
    },
  })

  const externalViewerMutation = useMutation({
    mutationFn: async ({ placeholder }: { placeholder: Window | null }) => {
      try {
        const res = await createAuditLogsPortalLink()
        return { portalUrl: res.data.portal_url, placeholder }
      } catch (err) {
        placeholder?.close()
        throw err
      }
    },
    onSuccess: ({ portalUrl, placeholder }) => {
      // Popup blockers only treat window.open as user-initiated inside the click handler;
      // by onSuccess (post-await) most browsers would silently block it. The click handler
      // opens a placeholder up front; here we just point it at the real URL. If the placeholder
      // is gone (closed or blocked), a second window.open here would no longer be user-initiated
      // and would just get blocked too — tell the user to retry instead.
      if (placeholder && !placeholder.closed) {
        placeholder.location.href = portalUrl
      } else {
        updateToast('Couldn’t open the events viewer. Please try again.', 'error', 5000)
      }
    },
    onError: () => {
      updateToast('Couldn’t open the events viewer. Please try again.', 'error', 5000)
    },
  })

  const handleOpenExternalViewerClick = () => {
    // Open synchronously, inside the click event, so the browser counts it as
    // user-initiated before the async call goes out to mint the portal URL.
    const placeholder = window.open('about:blank', '_blank')
    // Prevent the WorkOS-hosted portal from reaching back into this window via window.opener.
    if (placeholder) placeholder.opener = null
    externalViewerMutation.mutate({ placeholder })
  }

  const notOptedIn = isApiError(query.error) && query.error.status === 404
  const lastPageTruncated = !!query.data?.pages.at(-1)?.truncated

  const domainStatusQuery = useQuery({ ...domainVerificationsQuery(), enabled: notOptedIn })

  // A genuine read failure (never the "not opted in" 404) replaces the whole section with the
  // external-viewer entry point — no filters, no error text. This is deliberately presented as
  // the intended way to view events, not as a fallback for a broken feature. Only when there's
  // no loaded data at all — a failed background refresh or "Load more" on an already-populated
  // table leaves the existing rows visible instead of yanking them for the fallback.
  if (!noEventTypesSelected && query.isError && !notOptedIn && !query.data) {
    return (
      <div className={styles.section}>
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleOpenExternalViewerClick}
            disabled={externalViewerMutation.isPending}
          >
            {externalViewerMutation.isPending ? 'Opening…' : 'View audit events'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.section}>
      {!query.isLoading && (
        <div className={styles.filters}>
          <div className={styles.filterControls}>
            <DateRangePicker
              value={{ start: startDate, end: endDate }}
              onChange={({ start, end }) => {
                setStartDate(start)
                setEndDate(end)
              }}
              today={today}
              disabled={exportMutation.isPending}
            />
            <EventTypeFilter
              value={selectedEventTypes}
              onChange={setSelectedEventTypes}
              disabled={exportMutation.isPending}
            />
            <Button
              title={query.isFetching ? 'Refreshing…' : 'Refresh'}
              onClick={() => handleRefresh()}
              type="light"
              isLoading={query.isFetching}
              isDisabled={noEventTypesSelected}
            />
          </div>

          <div className={styles.actions}>
            <Button
              title={exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
              onClick={() => exportMutation.mutate('csv')}
              type="dark"
              isLoading={exportMutation.isPending}
              isDisabled={!!validationError || notOptedIn || noEventTypesSelected}
            />
            <Button
              title="Export JSON"
              onClick={() => exportMutation.mutate('json')}
              type="light"
              isDisabled={!!validationError || exportMutation.isPending || notOptedIn || noEventTypesSelected}
            />
          </div>
        </div>
      )}

      {validationError && <div className={styles.errorBanner}>{validationError}</div>}

      {noEventTypesSelected && <div className={styles.empty}>No event types selected.</div>}
      {!noEventTypesSelected && query.isLoading && <div className={styles.empty}>Loading…</div>}
      {!noEventTypesSelected && notOptedIn && (
        <div className={styles.empty}>
          <NotOptedInMessage
            domains={domainStatusQuery.data?.data.domains}
            domainsLoaded={domainStatusQuery.isSuccess}
          />
        </div>
      )}
      {!noEventTypesSelected && query.data && rows.length === 0 && (
        <div className={styles.empty}>No events found for this range.</div>
      )}
      {!noEventTypesSelected && query.data && rows.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Details</th>
                <th>Event ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.event_id}>
                  <td>{format(new Date(row.timestamp), 'MMM d, yyyy, h:mmaaa')}</td>
                  <td>{row.actor_name ?? row.actor_id ?? '—'}</td>
                  <td>{actionLabel(row.action)}</td>
                  <td>{row.target_name ?? row.target_id ?? '—'}</td>
                  <td>{getSettingChangeDetail(row) ?? '—'}</td>
                  <td>
                    <span className={styles.eventId} title={row.event_id}>
                      {row.event_id}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!noEventTypesSelected && query.data && (
        <div className={styles.loadMoreRow}>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => setPageSize(Number(v) as WorkosAuditEventsPageSize)}
            disabled={exportMutation.isPending}
          >
            <SelectTrigger className="data-[size=default]:h-10 w-auto cursor-pointer px-4" aria-label="Page size">
              <SelectValue>{pageSize} per page</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {WORKOS_AUDIT_EVENTS_PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {query.hasNextPage && (
            <Button
              title={query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              onClick={() => query.fetchNextPage()}
              type="light"
              isLoading={query.isFetchingNextPage}
            />
          )}
          {!query.hasNextPage && lastPageTruncated && (
            <span className={styles.empty}>
              Some older events share an exact timestamp and can&apos;t be reached — narrow the date range to see them.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default WorkosAuditEventsSection
